import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Row, Col, FormGroup, Label, Input, Button, Alert } from 'reactstrap';
import { addData, updateData, getIdData } from '../../lib/resourcecenter';
import { getDataWithTypeForSelect } from '../../lib/parameters';
import { getCurrentDateInput } from '../../utils/dateTimeUtils';

const ResourceModal = ({ isOpen, toggle, instructorId, editingResource, onSave, isDarkMode }) => {
    const [rdate, setRdate] = useState('');
    const [topic, setTopic] = useState('');
    const [parameters, setParameters] = useState([]);
    const [grade, setGrade] = useState('');
    const [howuse, setHowuse] = useState('');
    const [link1, setLink1] = useState('');
    const [shareWithStudents, setShareWithStudents] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const isEdit = !!editingResource;

    useEffect(() => {
        if (isOpen) {
            getParameterData();
            if (isEdit) {
                fetchResourceData();
            } else {
                setRdate(getCurrentDateInput());
                setTopic('');
                setGrade('');
                setHowuse('');
                setLink1('');
                setShareWithStudents(false);
            }
            setError('');
        }
    }, [isOpen, editingResource]);

    const getParameterData = async () => {
        try {
            const info = await getDataWithTypeForSelect('RESOURCE');
            setParameters(info);
        } catch (error) {
            console.error('Error fetching parameters:', error);
            setError('Failed to load parameters');
        }
    };

    const fetchResourceData = async () => {
        try {
            setLoading(true);
            const data = await getIdData(editingResource.id);
            if (data && data.length > 0) {
                setRdate(data[0].rdate || getCurrentDateInput());
                setTopic(data[0].topic || '');
                setGrade(data[0].grade || '');
                setHowuse(data[0].howuse || '');
                setLink1(data[0].link1 || '');
                // Note: shareWithStudents isn't clearly mapped in getIdData but the form had it
            }
        } catch (error) {
            console.error('Error fetching resource data:', error);
            setError('Failed to load resource data');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        const payload = {
            instructorId: instructorId,
            rdate: new Date(rdate),
            topic: topic,
            grade: grade,
            howuse: howuse,
            link1: link1
        };

        try {
            if (isEdit) {
                await updateData({ ...payload, id: editingResource.id });
            } else {
                await addData(payload);
            }
            if (onSave) await onSave();
            toggle();
        } catch (error) {
            console.error('Error saving resource:', error);
            setError('Failed to save resource');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg" className={isDarkMode ? 'dark-modal' : ''}>
            <ModalHeader toggle={toggle}>
                {isEdit ? 'Update Resource' : 'Add Resource'}
            </ModalHeader>
            <ModalBody>
                {error && <Alert color="danger">{error}</Alert>}
                <form onSubmit={handleSubmit} id="resource-modal-form">
                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label">Date</Label>
                                <Input type="date" value={rdate} onChange={(e) => setRdate(e.target.value)} required disabled={loading} />
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label">Topic</Label>
                                <Input type="select" value={topic} onChange={(e) => setTopic(e.target.value)} required disabled={loading}>
                                    <option value="">Select Topic</option>
                                    {parameters.map((param) => (
                                        <option key={param.value} value={param.value}>{param.label}</option>
                                    ))}
                                </Input>
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label">Grade</Label>
                                <Input type="select" value={grade} onChange={(e) => setGrade(e.target.value)} required disabled={loading}>
                                    <option value="">Select Grade</option>
                                    <option value="12">12</option>
                                    <option value="9-11">9-11</option>
                                    <option value="All">All</option>
                                </Input>
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label">Link</Label>
                                <Input type="url" value={link1} onChange={(e) => setLink1(e.target.value)} placeholder="Enter resource link" disabled={loading} />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="12">
                            <FormGroup>
                                <Label className="form-control-label">How to use it</Label>
                                <Input
                                    type="textarea"
                                    value={howuse}
                                    onChange={(e) => setHowuse(e.target.value)}
                                    placeholder="Describe how to use this resource"
                                    rows="4"
                                    required
                                    disabled={loading}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="12">
                            <FormGroup>
                                <div className="form-check form-switch">
                                    <Input
                                        type="checkbox"
                                        checked={shareWithStudents}
                                        onChange={(e) => setShareWithStudents(e.target.checked)}
                                        disabled={loading}
                                    />
                                    <Label className="form-check-label">Share with my students</Label>
                                </div>
                            </FormGroup>
                        </Col>
                    </Row>
                </form>
            </ModalBody>
            <ModalFooter>
                <Button color="primary" type="submit" form="resource-modal-form" disabled={loading}>
                    {loading ? 'Saving...' : isEdit ? 'Update Resource' : 'Add Resource'}
                </Button>
                <Button color="secondary" onClick={toggle} disabled={loading}>
                    Cancel
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default ResourceModal;
